import React, { useState } from 'react';
import { Mail, MapPin, Phone, Send } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { submitContact } from '../lib/api';
import { Toaster, toast } from 'sonner';

export const ContactPage = () => {
  const { t } = useApp();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact(form);
      toast.success(t('contact.form.success'));
      setForm({ name: '', email: '', company: '', phone: '', message: '' });
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="contact-page">
      <Toaster position="top-right" theme="dark" />
      
      {/* Hero */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00D4FF]/10 text-[#00D4FF] text-sm font-medium mb-6">
            <Mail className="w-4 h-4" />
            Get In Touch
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" data-testid="contact-title">
            {t('contact.title')}
          </h1>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold mb-8">{t('contact.contactInfo')}</h2>
              
              <div className="space-y-6">
                {/* Email */}
                <a
                  href="mailto:sales@luxortek.com"
                  className="card-dark p-6 flex items-center gap-4 group hover:border-[#00D4FF]/30 transition-all"
                  data-testid="contact-email-card"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-[#00D4FF]/10 text-[#00D4FF] group-hover:bg-[#00D4FF] group-hover:text-black transition-all">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="text-white/50 text-sm">{t('contact.email')}</div>
                    <div className="text-white font-medium">sales@luxortek.com</div>
                  </div>
                </a>

                {/* Location */}
                <div className="card-dark p-6 flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#F59E0B]/10 text-[#F59E0B]">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="text-white/50 text-sm">{t('contact.exportHub')}</div>
                    <div className="text-white font-medium">Miami, Florida, USA</div>
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="mt-12">
                <h3 className="text-lg font-semibold mb-4">{t('contact.businessHours')}</h3>
                <div className="text-white/60 space-y-1">
                  <p>{t('contact.monFri')}</p>
                  <p>{t('contact.saturday')}</p>
                  <p>{t('contact.sundayClosed')}</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="card-dark p-8">
              <h2 className="text-2xl font-bold mb-8">{t('contact.sendMessage')}</h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white/60 text-sm mb-2">{t('contact.form.name')} *</label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF]"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-2">{t('contact.form.email')} *</label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF]"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white/60 text-sm mb-2">{t('contact.form.company')}</label>
                    <input
                      type="text"
                      value={form.company}
                      onChange={(e) => setForm({ ...form, company: e.target.value })}
                      className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF]"
                      data-testid="contact-company-input"
                    />
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-2">{t('contact.form.phone')}</label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF]"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white/60 text-sm mb-2">{t('contact.form.message')} *</label>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF] resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="contact-submit-btn"
                >
                  {loading ? 'Sending...' : (
                    <>
                      <Send className="w-4 h-4" />
                      {t('contact.form.submit')}
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
